import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Select, ConfigProvider } from 'antd';
import { ShoppingCartOutlined, EditOutlined } from '@ant-design/icons';
import { addToCart, updateCartItem } from '../../features/cart';
import {
    hasPackagingOptions,
    hasCityValues,
    getUniquePackagings,
    getCitiesForPackaging,
    findOption,
    getSelectLabels,
    isFirstSelectSearchable,
} from '../../utils/packagingHelpers';
import './styles.css';

const safeDecodeURI = (str) => { try { return decodeURIComponent(str); } catch { return str; } };
const toSelectOptions = (options) => options.map(opt => ({ value: opt, label: safeDecodeURI(opt) }));
const formatPrice = (value) => { const num = Math.round(Number(value)); if (isNaN(num)) return null; return `${num.toLocaleString('ru-RU')}₽`; };

// Fallback: для товаров БЕЗ packaging_options вообще
const getVariationAttributes = (attributes) => {
    if (!Array.isArray(attributes)) return [];
    return attributes.filter(a => a.variation === true && a.options?.length > 0 && a.slug !== 'pa_warehouse');
};
const fallbackAttrLabels = { 'pa_package-type': 'Выберите упаковку', 'pa_warehouse': 'Выберите склад отгрузки' };
const getFallbackLabel = (attr) => fallbackAttrLabels[attr.slug] || `Выберите ${attr.name.toLowerCase()}`;

const VariationModal = ({ open, onClose, product, mode = 'add' }) => {
    const dispatch = useDispatch();
    const [selectedPackaging, setSelectedPackaging] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedVariations, setSelectedVariations] = useState({});

    const isEditMode = mode === 'edit';
    const useNewLogic = hasPackagingOptions(product);

    // === Новая логика (packaging_options) ===
    const showCitySelect = useMemo(
        () => useNewLogic && hasCityValues(product?.packaging_options),
        [product?.packaging_options, useNewLogic]
    );

    const { firstLabel, secondLabel } = useMemo(
        () => useNewLogic ? getSelectLabels(product) : { firstLabel: '', secondLabel: '' },
        [product?.attributes, useNewLogic]
    );

    const searchableFirst = useMemo(
        () => useNewLogic && isFirstSelectSearchable(product),
        [product?.attributes, useNewLogic]
    );

    const packagings = useMemo(
        () => useNewLogic ? getUniquePackagings(product?.packaging_options) : [],
        [product?.packaging_options, useNewLogic]
    );

    const availableCities = useMemo(() => {
        if (!useNewLogic || !showCitySelect || !selectedPackaging) return [];
        return getCitiesForPackaging(product?.packaging_options, selectedPackaging);
    }, [product?.packaging_options, selectedPackaging, useNewLogic, showCitySelect]);

    const matchedOption = useMemo(() => {
        if (!useNewLogic || !selectedPackaging) return null;
        if (showCitySelect) {
            return selectedCity ? findOption(product?.packaging_options, selectedPackaging, selectedCity) : null;
        }
        return findOption(product?.packaging_options, selectedPackaging, null);
    }, [product?.packaging_options, selectedPackaging, selectedCity, useNewLogic, showCitySelect]);

    // === Fallback (без packaging_options) ===
    const variationAttrs = useMemo(
        () => !useNewLogic ? getVariationAttributes(product?.attributes) : [],
        [product?.attributes, useNewLogic]
    );

    // === Инициализация ===
    useEffect(() => {
        if (!open || !product) return;
        if (useNewLogic) {
            if (isEditMode) {
                setSelectedPackaging(product.packaging || null);
                setSelectedCity(product.warehouse || null);
            } else if (packagings.length > 0) {
                const firstPkg = packagings[0];
                setSelectedPackaging(firstPkg);
                if (showCitySelect) {
                    const cities = getCitiesForPackaging(product.packaging_options, firstPkg);
                    setSelectedCity(cities.length > 0 ? cities[0] : null);
                }
            }
        } else {
            if (isEditMode && product.selectedVariations) {
                setSelectedVariations({ ...product.selectedVariations });
            } else {
                const defaults = {};
                variationAttrs.forEach(attr => {
                    const da = product.default_attributes?.find(d => d.id === attr.id || d.name === attr.name);
                    if (da && attr.options.length > 0) {
                        const match = attr.options.find(opt => opt.toLowerCase().includes((da.option || '').replace(/-/g, ' ').toLowerCase()));
                        defaults[attr.slug] = match || attr.options[0];
                    } else if (attr.options.length > 0) {
                        defaults[attr.slug] = attr.options[0];
                    }
                });
                setSelectedVariations(defaults);
            }
        }
    }, [open, product?.id, isEditMode]);

    const handlePackagingChange = (value) => {
        setSelectedPackaging(value);
        if (showCitySelect) {
            const cities = getCitiesForPackaging(product?.packaging_options, value);
            setSelectedCity(cities.length > 0 ? cities[0] : null);
        }
    };

    const allSelected = useMemo(() => {
        if (useNewLogic) {
            if (!selectedPackaging) return false;
            if (showCitySelect && !selectedCity) return false;
            return !!matchedOption;
        }
        return variationAttrs.every(attr => !!selectedVariations[attr.slug]);
    }, [useNewLogic, selectedPackaging, selectedCity, showCitySelect, matchedOption, variationAttrs, selectedVariations]);

    const displayPrice = useNewLogic && matchedOption ? formatPrice(matchedOption.price) : formatPrice(product?.price);

    const handleConfirm = () => {
        if (!allSelected) return;
        if (useNewLogic) {
            const data = {
                packaging: selectedPackaging || '',
                warehouse: selectedCity || '',
                variationId: matchedOption.variation_id,
                price: matchedOption.price,
                selectedVariations: {
                    packaging: selectedPackaging,
                    ...(selectedCity ? { city: selectedCity } : {}),
                },
            };
            isEditMode ? dispatch(updateCartItem(product.cartItemId, data)) : dispatch(addToCart({ ...product, ...data }, 1));
        } else {
            const data = {
                packaging: selectedVariations['pa_package-type'] || '',
                warehouse: selectedVariations['pa_warehouse'] || '',
                selectedVariations: { ...selectedVariations },
                variationId: Object.values(selectedVariations).filter(Boolean).join('_') || null,
            };
            isEditMode ? dispatch(updateCartItem(product.cartItemId, data)) : dispatch(addToCart({ ...product, ...data }, 1));
        }
        onClose();
    };

    if (!product) return null;

    return (
        <ConfigProvider theme={{ components: { Modal: { contentBg: '#1d1d1d', headerBg: '#1d1d1d', titleColor: '#fff', colorIcon: '#999', colorIconHover: '#fff' }, Select: { colorBgContainer: '#2a2a2a', colorBorder: '#444', colorText: '#fff', colorTextPlaceholder: '#888', optionSelectedBg: '#DC0202', optionSelectedColor: '#fff', colorBgElevated: '#2a2a2a', optionActiveBg: '#333', colorTextQuaternary: '#888', selectorBg: '#2a2a2a' } } }}>
            <Modal open={open} onCancel={onClose} title={null} footer={null} centered width={400} className="variation-modal" destroyOnHidden>
                <div className="variation-modal-body">
                    <div className="variation-modal-product">
                        {(product.images?.[0]?.src || product.main_image) && <img className="variation-modal-img" src={product.images?.[0]?.src || product.main_image} alt={product.name} loading="lazy" />}
                        <div className="variation-modal-info">
                            <div className="variation-modal-name">{product.name}</div>
                            <div className="variation-modal-price">{displayPrice}</div>
                        </div>
                    </div>

                    <div className="variation-modal-fields">
                        {useNewLogic ? (
                            <>
                                <div className="variation-modal-field">
                                    <label className="variation-modal-label">{firstLabel}</label>
                                    <Select
                                        value={selectedPackaging || undefined}
                                        onChange={handlePackagingChange}
                                        style={{ width: '100%' }}
                                        options={toSelectOptions(packagings)}
                                        placeholder={firstLabel}
                                        size="large"
                                        notFoundContent="Нет данных"
                                        {...(searchableFirst ? {
                                            showSearch: true,
                                            filterOption: (input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
                                        } : {})}
                                    />
                                </div>

                                {showCitySelect && (
                                    <div className="variation-modal-field">
                                        <label className="variation-modal-label">{secondLabel}</label>
                                        <Select
                                            value={selectedCity || undefined}
                                            onChange={(val) => setSelectedCity(val)}
                                            style={{ width: '100%' }}
                                            options={toSelectOptions(availableCities)}
                                            placeholder={secondLabel}
                                            size="large"
                                            showSearch
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                            disabled={!selectedPackaging}
                                            notFoundContent="Нет данных"
                                        />
                                    </div>
                                )}

                                {matchedOption && matchedOption.stock_status !== 'instock' && (
                                    <div className="variation-modal-matched-price">
                                        <span className="variation-modal-out-of-stock">Нет в наличии</span>
                                    </div>
                                )}
                            </>
                        ) : (
                            variationAttrs.map(attr => (
                                <div className="variation-modal-field" key={attr.slug}>
                                    <label className="variation-modal-label">{getFallbackLabel(attr)}</label>
                                    <Select value={selectedVariations[attr.slug] || undefined} onChange={(val) => setSelectedVariations(prev => ({ ...prev, [attr.slug]: val }))} style={{ width: '100%' }} options={toSelectOptions(attr.options)} placeholder={getFallbackLabel(attr)} size="large" notFoundContent="Нет данных" {...(attr.slug === 'pa_warehouse' ? { showSearch: true, filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) } : {})} />
                                </div>
                            ))
                        )}
                    </div>

                    <button className={`variation-modal-btn ${!allSelected ? 'disabled' : ''}`} onClick={handleConfirm} disabled={!allSelected}>
                        {isEditMode ? (<><EditOutlined style={{ marginRight: 8, fontSize: 18 }} />Сохранить изменения</>) : (<><ShoppingCartOutlined style={{ marginRight: 8, fontSize: 18 }} />Добавить в корзину</>)}
                    </button>
                </div>
            </Modal>
        </ConfigProvider>
    );
};

export default VariationModal;

export const hasVariations = (product) => {
    if (hasPackagingOptions(product)) return true;
    return getVariationAttributes(product?.attributes).length > 0;
};